import { Navigate, Route } from "react-router-dom";

import { queryClient } from "@/providers/QueryClient";
import { loadSearchTablePreferences } from "@/shared/domain/config/loaders/loadSearchTablePreferences";
import { featureFlagRoute } from "@/shared/routing/featureFlagRoute";
import { InheritOutletContext } from "@/shared/routing/InheritOutletContext";

import { Device } from "./Device";
import { DeviceDatasets } from "./DeviceDatasets";
import { DeviceDetails } from "./DeviceDetails";
import { DeviceFiles } from "./DeviceFiles";
import { DeviceMetrics } from "./DeviceMetrics";
import { Devices } from "./Devices";
import { DeviceSessions } from "./DeviceSessions";
import { DeviceTokens } from "./DeviceTokens";

export const DevicesRoutes = () => [
  /*
   * Opening the devices list must seed saved column order / page size
   * before <Devices /> mounts — `useResourceView` snapshots `preferences`
   * once via `React.useState` and never re-reads.
   */
  <Route
    key="devices-preferences-layout"
    loader={loadSearchTablePreferences(
      queryClient,
      "device_search_table_preferences",
    )}
    element={<InheritOutletContext />}
  >
    <Route index element={<Devices />} />
  </Route>,
  <Route key={"id"} path=":deviceId" element={<Device />}>
    {/* Bare `/devices/:deviceId` has no tab to render; redirect to the default. */}
    <Route index element={<Navigate to="datasets" replace />} />
    <Route path="datasets" element={<DeviceDatasets />} />
    <Route path="files" element={<DeviceFiles />} />
    {/*
        Opening the Sessions tab must seed saved column order / page size
        before <DeviceSessions /> mounts — `useResourceView` snapshots
        `preferences` once via `React.useState` and never re-reads.
        `featureFlagRoute` already uses its loader slot for the flag
        gate, so this pathless wrapper Route runs the preferences loader.
    */}
    <Route
      key="sessions-preferences-layout"
      loader={loadSearchTablePreferences(
        queryClient,
        "session_search_table_preferences",
      )}
      element={<InheritOutletContext />}
    >
      {featureFlagRoute({
        path: "sessions",
        flagName: "release-sessions",
        redirectTo: ({ params }) => `/devices/${params.deviceId}/datasets`,
        element: <DeviceSessions />,
      })}
    </Route>
    {featureFlagRoute({
      path: "metrics",
      flagName: "release-metrics",
      redirectTo: ({ params }) => `/devices/${params.deviceId}/datasets`,
      element: <DeviceMetrics />,
    })}
    <Route path="details" element={<DeviceDetails />} />
    <Route path="tokens" element={<DeviceTokens />} />
    {/* Unknown subpath (typo, removed tab, stale bookmark): redirect to the default. */}
    <Route path="*" element={<Navigate to="datasets" replace />} />
  </Route>,
];
